import React, { useState } from 'react';
import { Box, Typography, Fade } from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { getFieldValidationRules, getFieldExample } from '../../utils/validationMessages';

interface FieldHintProps {
    fieldName: string;
    isFocused?: boolean;
    disabled?: boolean;
}

/**
 * FieldHint component displays validation rules when field is focused
 * Provides proactive guidance to prevent validation errors
 * 
 * Features:
 * - Shows validation rules on field focus
 * - Displays example values
 * - Smooth fade-in/out animation
 * - Disabled state support
 */
export const FieldHint: React.FC<FieldHintProps> = ({
    fieldName,
    isFocused = false,
    disabled = false,
}) => {
    const rules = getFieldValidationRules(fieldName);
    const example = getFieldExample(fieldName);

    // Don't render if no validation rules or disabled
    if (rules.length === 0 || disabled) {
        return null;
    }

    return (
        <Fade in={isFocused} timeout={200}>
            <Box
                sx={{
                    display: isFocused ? 'flex' : 'none',
                    alignItems: 'flex-start',
                    gap: 0.5,
                    mt: 0.5,
                    p: 1,
                    backgroundColor: 'info.lighter',
                    borderLeft: 2,
                    borderColor: 'info.main',
                    borderRadius: 0.5,
                }}
            >
                <InfoIcon
                    sx={{
                        fontSize: 16,
                        color: 'info.main',
                        mt: 0.1,
                        flexShrink: 0,
                    }}
                />
                <Box sx={{ flex: 1 }}>
                    {rules.map((rule, index) => (
                        <Typography
                            key={index}
                            variant="caption"
                            sx={{
                                display: 'block',
                                color: 'text.secondary',
                                lineHeight: 1.5,
                            }}
                        >
                            • {rule}
                        </Typography>
                    ))}
                    {example && (
                        <Typography
                            variant="caption"
                            sx={{
                                display: 'block',
                                color: 'text.secondary',
                                fontStyle: 'italic',
                                mt: 0.5,
                            }}
                        >
                            {example}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Fade>
    );
};
